import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
	FrameStatus,
	DomElement,
	Frame,
	TransformVisualEdit,
	TextVisualEdit,
	ImageVisualEdit,
	DeleteVisualEdit,
	VisualEdit,
} from 'dragonmouse-shared/interfaces';
import { ImageElement, ToStudio, SelectionInfo } from 'dragonmouse-shared/devigner';
import { trackVisualEditAction } from 'analytics';
import ImageSelectedModal from 'components/Modal/ImageSelectedModal';
import { computeViewport, Size, Viewport } from './util.ts';
import FrameLoadingIndicator from 'components/Widget/FrameLoadingIndicator';
import { Loader } from 'components/Widget/Loader';
import Button, { ButtonType } from 'components/Widget/Button/Button';
import forge from 'apis/forge';
import * as S from './styles.ts';
import { DEFAULT_CANVAS_COLOR, selectActiveFrameChat, selectActiveScene } from 'redux/db/reducers.ts';
import { shallowEqual, useSelector } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { frameImageUrl } from 'utils';
import { useImageLoading } from 'hooks/useImageLoading';
import { useFrameMoonProgress } from 'hooks/useFrameMoonProgress';
import { isForgeOrigin, sendToDevigner } from '../../../devigner.ts';
import { useEventListener } from 'events/EventManager';
import { Point } from 'dragonmouse-shared/geometry.ts';
import {
	findVisualEditEntry,
	pushEditToIframe,
	removeEditFromFrame,
	saveEditToFrame,
} from 'visualEdits';

interface PrototypeProps {
	frame: Frame;
	siteUrl: string | null;
	isDragging: boolean;
	inEditMode: boolean;
	fullscreenMode: boolean;
	onSelection: (selectionInfo: SelectionInfo) => void;
	onSetInEditMode: (v: boolean) => void;
	onSetSelectedElements: (elements: DomElement[]) => void;
	onIframeReadyChange: (ready: boolean) => void;
	onContextMenu?: (selectionInfo: SelectionInfo, point: Point) => void;
	prototypeFrameSize?: { width: number; height: number };
	onSetFrameSize?: (size: { width: number; height: number }) => void;
	iframeReloadKey?: number;
	activeMode?: string | null;
}

// Screen-px sizes for the status overlay. It lives inside the frame's scale transform, so each
// size is divided by the viewport scale to keep the indicator constant on screen at any zoom.
// The proportions mirror the canvas (Canvas/FrameView).
const MOON_SIZE = 72;
const STATUS_SIZE = 22;
const STATUS_GAP = 28;

const Prototype = ({
	frame,
	siteUrl,
	isDragging,
	inEditMode,
	fullscreenMode,
	onSelection,
	onSetInEditMode,
	onSetSelectedElements,
	onIframeReadyChange,
	onContextMenu,
	prototypeFrameSize,
	onSetFrameSize,
	iframeReloadKey,
	activeMode,
}: PrototypeProps) => {
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const frameRef = useRef(frame);
	const [iframeReady, setIframeReady] = useState(false);
	const [currentFrameSize, setCurrentFrameSize] = useState<Size>({
		width: prototypeFrameSize?.width ?? frame.width ?? 0,
		height: prototypeFrameSize?.height ?? frame.height ?? 0,
	});
	const [isResizing, setIsResizing] = useState(false);
	const [preservedScale, setPreservedScale] = useState<number | null>(null);
	const [containerSize, setContainerSize] = useState<Size>({ width: 0, height: 0 });
	const [viewport, setViewport] = useState<Viewport | null>(null);
	const [isApplyingVisualEdits, setIsApplyingVisualEdits] = useState(false);
	const [imageReplaceTarget, setImageReplaceTarget] = useState<ImageElement | null>(null);

	const imageSrc = frameImageUrl(frame);
	const [isImageLoading, handleImageRef] = useImageLoading(imageSrc ?? '');

	// Held here rather than inside the indicator: the status overlay unmounts for a beat when the
	// frame turns READY before its new <img> mounts, which would throw away the accumulated progress.
	const moon = useFrameMoonProgress(frame, isImageLoading);

	const { scene, prototypeScaling } = useSelector((r: RootReducer) => {
		return {
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
			prototypeScaling: r.mode.prototypeScaling,
		};
	}, shallowEqual);

	// True once the frame chat loads with a VE entry for this frame's site.
	// Drives the initIframeFromFrameEdits effect: false → true re-triggers it
	// so visual edits are applied after the chat data arrives.
	const hasVeDataInChat = useSelector((r: RootReducer) => {
		if (!frame?.metadata?.visualEditActive) return false;
		const fc = selectActiveFrameChat(r.db);
		if (!fc || fc.frameId !== frame.id) return false;
		return fc.entries.some((e) => e.kind === 'site_ve' && e.siteId === frame.siteId);
	});

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------

	const syncToolToDevigner = (siteId: string, inEditMode: boolean) => {
		if (inEditMode) {
			sendToDevigner(iframeRef.current, siteId, {
				type: 'startOverlay',
				tool: 'select',
			});
		} else {
			sendToDevigner(iframeRef.current, siteId, {
				type: 'stopOverlay',
			});
		}
	};

	useEffect(() => {
		if (!frame?.siteId || !iframeReady) return;
		sendToDevigner(iframeRef.current, frame.siteId, {
			type: 'setMode',
			mode: activeMode ?? '',
		});
	}, [activeMode, frame?.siteId, iframeReady]);

	// Watch container size changes
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				const { width, height } = entry.contentRect;
				setContainerSize({ width, height });
			}
		});

		resizeObserver.observe(container);

		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	// Re-initialize currentFrameSize when frame changes
	useEffect(() => {
		setCurrentFrameSize({ width: frame.width, height: frame.height });
	}, [frame.width, frame.height]);

	// Sync with prototypeFrameSize prop when it changes
	useEffect(() => {
		if (prototypeFrameSize) {
			setCurrentFrameSize({ width: prototypeFrameSize.width, height: prototypeFrameSize.height });
		}
	}, [prototypeFrameSize?.width, prototypeFrameSize?.height]);

	// Recalculate viewport when dependencies change
	useEffect(() => {
		if (containerSize.width === 0 || containerSize.height === 0) return; // Wait for container to be measured

		const newViewport = computeViewport(
			currentFrameSize,
			containerSize,
			prototypeScaling,
			fullscreenMode
		);
		setViewport(newViewport);
	}, [currentFrameSize, containerSize, prototypeScaling, fullscreenMode, preservedScale]);

	// Message handler ref — updated every render so the persistent listener
	// always has access to fresh props/state without needing to be torn down.
	const messageHandlerRef = useRef<(toStudio: ToStudio) => void>(() => {});
	messageHandlerRef.current = (toStudio: ToStudio) => {
		const iframeRect = iframeRef.current?.getBoundingClientRect();
		const currentFrame = frameRef.current;
		const msgSiteId = toStudio.siteId;

		// Commit messages: always process, using the message's siteId to find the right entry.
		// These must never be dropped, even if the user switched versions since the edit began.
		// The iframe already reflects these edits (devigner just applied them locally), so we
		// only persist — no pushEditToIframe.
		if (toStudio.type === 'dragStart') {
			if (!currentFrame) return;
			const transformEdit: TransformVisualEdit = {
				kind: 'transform',
				elementId: toStudio.elementId,
				transform: { x: 0, y: 0 },
			};
			saveEditToFrame(
				currentFrame.id,
				transformEdit,
				(edit) => edit.kind === 'transform' && edit.elementId === toStudio.elementId,
				msgSiteId
			);
			return;
		} else if (toStudio.type === 'dragEnd' || toStudio.type === 'transformCommit') {
			if (!currentFrame) return;
			const transformEdit: TransformVisualEdit = {
				kind: 'transform',
				elementId: toStudio.elementId,
				transform: toStudio.finalTransform,
			};
			saveEditToFrame(
				currentFrame.id,
				transformEdit,
				(edit) => edit.kind === 'transform' && edit.elementId === toStudio.elementId,
				msgSiteId
			);
			return;
		} else if (toStudio.type === 'textEditEnd') {
			if (!currentFrame) return;
			if (toStudio.finalInnerHTML === toStudio.originalInnerHTML) {
				trackVisualEditAction('remove_text_edit');
				removeEditFromFrame(
					currentFrame.id,
					(edit) => edit.kind === 'text' && edit.elementId === toStudio.elementId,
					msgSiteId
				);
				return;
			}
			const textEdit: TextVisualEdit = {
				kind: 'text',
				elementId: toStudio.elementId,
				innerHTML: toStudio.finalInnerHTML,
			};
			trackVisualEditAction('commit_text_edit');
			saveEditToFrame(
				currentFrame.id,
				textEdit,
				(edit) => edit.kind === 'text' && edit.elementId === toStudio.elementId,
				msgSiteId
			);
			return;
		} else if (toStudio.type === 'deleteCommit') {
			if (!currentFrame) return;
			const deleteEdit: DeleteVisualEdit = {
				kind: 'delete',
				elementId: toStudio.elementId,
			};
			trackVisualEditAction('delete_element');
			saveEditToFrame(
				currentFrame.id,
				deleteEdit,
				(edit) => edit.kind === 'delete' && edit.elementId === toStudio.elementId,
				msgSiteId
			);
			// Devigner already cleared its own selectedElement; clear studio-side
			// selection too so the panel returns to no-selection state.
			onSetSelectedElements([]);
			return;
		}

		// Interactive messages: only process for the current siteId
		if (msgSiteId !== currentFrame?.siteId) return;

		if (toStudio.type === 'contextMenu') {
			if (!viewport) return;
			if (!iframeRect) return;
			const point = {
				x: toStudio.point.x,
				y: toStudio.point.y,
			};
			const adjustedPoint = {
				x: iframeRect.left + point.x * viewport.scale,
				y: iframeRect.top + point.y * viewport.scale + 25,
			};
			if (toStudio.selectionInfo) {
				onContextMenu?.(toStudio.selectionInfo, adjustedPoint);
			}
		} else if (toStudio.type === 'selection') {
			onSelection(toStudio.selectionInfo);
			// Pull keyboard focus into the iframe so devigner's selectTool can
			// receive arrow-key nudges. devigner's mousedown handler calls
			// e.preventDefault() to suppress text selection, which also blocks
			// the browser's default focus transfer to the iframe element.
			// Sync call covers immediate; rAF covers the case where a panel
			// re-render after onSelection mounts something that grabs focus.
			iframeRef.current?.focus();
			requestAnimationFrame(() => {
				iframeRef.current?.focus();
			});
		} else if (toStudio.type === 'ready') {
			if (currentFrame?.siteId) {
				syncToolToDevigner(currentFrame.siteId, inEditMode);
			}
		} else if (toStudio.type === 'keyPress') {
			// handle key presses from within the iframe
			if (toStudio.key === 'Escape') {
				// Exit edit mode (devigner handles drag cancellation internally)
				onSetInEditMode(false);
				onSetSelectedElements([]);
			}
		} else if (toStudio.type === 'textEditStart') {
			// when devigner initiates a text edit, it currently always comes from a double-click
			trackVisualEditAction('edit_text_via_dblclick');
		} else if (toStudio.type === 'devignerReady') {
			setIframeReady(true);
		}
	};

	// Persistent listener — lives for the component's lifetime, never torn down.
	// Delegates to messageHandlerRef which always has fresh values.
	useEffect(() => {
		const listener = (event: MessageEvent) => {
			if (!isForgeOrigin(event)) return;
			if (event.source !== iframeRef.current?.contentWindow) return;
			messageHandlerRef.current(event.data as ToStudio);
		};
		window.addEventListener('message', listener);
		return () => window.removeEventListener('message', listener);
	}, []);

	useEffect(() => {
		if (!siteUrl || !frame?.siteId) return;
		syncToolToDevigner(frame.siteId, inEditMode);
	}, [inEditMode, siteUrl, frame.siteId]);

	useEventListener('devigner-clear-selection', () => {
		if (frame?.siteId) {
			sendToDevigner(iframeRef.current, frame.siteId, { type: 'clearSelection' });
		}
	});

	useEventListener('devigner-select-element', (elementId) => {
		if (frame?.siteId) {
			sendToDevigner(iframeRef.current, frame.siteId, { type: 'selectElement', elementId });
		}
	});

	// Studio-originated visual edits (from panel controls) need to be pushed to the iframe;
	// the iframe doesn't otherwise receive them since initIframeFromFrameEdits below only
	// fires on init events. Filter by frameId so we don't react to other frames' edits.
	useEventListener('apply-visual-edit-to-iframe', async ({ frameId, edit }) => {
		if (frameId !== frame?.id || !frame.siteId) return;
		const converted = await resolveSignedImageUrls(frame.id, frame.siteId, [edit]);
		sendToDevigner(iframeRef.current, frame.siteId, {
			type: 'applyVisualEdits',
			visualEdits: converted,
		});
	});

	useEventListener('open-image-replace-modal', ({ frameId, imageElement }) => {
		if (frameId !== frame?.id) return;
		setImageReplaceTarget(imageElement);
	});

	useEventListener('enter-text-edit', ({ frameId, elementId }) => {
		if (frameId !== frame?.id || !frame.siteId) return;
		sendToDevigner(iframeRef.current, frame.siteId, { type: 'startTextEdit', elementId });
	});

	// Init the iframe with the frame's saved edits on initialization events only
	// (not on entry mutations). The devigner already has the correct state after a
	// user edit — re-applying from server state would echo stale data back, and
	// panel-side edits push themselves to the iframe via apply-visual-edit-to-iframe.
	useEffect(() => {
		initIframeFromFrameEdits(frame);
	}, [frame?.id, frame?.metadata?.visualEditActive, frame?.siteId, iframeReady, siteUrl, hasVeDataInChat]);

	// Iframe needs re-initialization when siteUrl changes or iframe is reloaded via key
	useEffect(() => {
		setIframeReady(false);
	}, [siteUrl, iframeReloadKey]);

	// Notify parent when iframe ready state changes
	// For images (when siteUrl is null), consider them ready when not loading
	useEffect(() => {
		if (siteUrl) {
			onIframeReadyChange(iframeReady);
		} else {
			// For images, we're ready when the image is not loading
			onIframeReadyChange(!isImageLoading);
		}
	}, [iframeReady, isImageLoading, siteUrl, onIframeReadyChange]);

	// Store current scale when view method changes
	useEffect(() => {
		if (frame && viewport) {
			// Store the current scale for preservation
			setPreservedScale(viewport?.scale);
		}
	}, [prototypeScaling, frame, viewport?.scale]);

	//------------------------------------------------------------------------------------------------
	// UTILITY
	//------------------------------------------------------------------------------------------------

	// Helper function to update frame size and notify parent
	const updateFrameSize = (newSize: { width: number; height: number }) => {
		setCurrentFrameSize(newSize);
		onSetFrameSize?.(newSize);
	};

	// Image visual edits hold storage paths (e.g. "images/abc.png"), not URLs the
	// iframe can load. Round-trip via forge.getFrameSignedUrls to substitute. Returns
	// the input untouched if no image edits have storage paths (avoids the round trip).
	const resolveSignedImageUrls = async (
		frameId: string,
		siteId: string,
		edits: VisualEdit[]
	): Promise<VisualEdit[]> => {
		const hasStoragePaths = edits.some(
			(edit) => edit.kind === 'image' && edit.imageUrl && !edit.imageUrl.match(/^https?:\/\//)
		);
		if (!hasStoragePaths) return edits;

		try {
			const response = await forge.getFrameSignedUrls({ frameId, siteId });
			const urlMap = response.urlMap;
			return edits.map((edit) => {
				if (edit.kind === 'image') {
					const signedUrl = urlMap[edit.imageUrl];
					if (signedUrl) return { ...edit, imageUrl: signedUrl } as ImageVisualEdit;
				}
				return edit;
			});
		} catch (error) {
			console.error('Failed to get signed URLs:', error);
			Sentry.captureException(error);
			return edits;
		}
	};

	const onImageReplaceSubmit = async (imageUrl: string, altTag: string) => {
		if (!imageReplaceTarget || !frame.siteId) return;
		const elementId = imageReplaceTarget.elementId;
		const imageEdit: ImageVisualEdit = {
			kind: 'image',
			elementId,
			imageUrl,
			altTag,
		};
		trackVisualEditAction('commit_image_edit');
		setImageReplaceTarget(null);
		// Must await: pushEditToIframe triggers a signed-URL fetch that reads the
		// just-persisted entry from the server. Racing them returns stale state.
		await saveEditToFrame(
			frame.id,
			imageEdit,
			(edit) => edit.kind === 'image' && edit.elementId === elementId,
			frame.siteId
		);
		pushEditToIframe(frame.id, imageEdit);
	};

	// Bulk-apply (or clear) the frame's saved visual edits on the iframe.
	// Distinct from pushEditToIframe (single edit, panel-driven): this runs at
	// init/lifecycle events and shows a loading overlay during signed-URL fetch.
	const initIframeFromFrameEdits = useCallback(
		async (currentFrame: Frame) => {
			if (!siteUrl || !currentFrame?.siteId || !iframeReady) return;

			const visualEditActive = currentFrame.metadata?.visualEditActive;

			if (visualEditActive) {
				const visualEditEntry = findVisualEditEntry(currentFrame, currentFrame.siteId);

				if (visualEditEntry && visualEditEntry.visualEdits.length > 0 && currentFrame.id) {
					setIsApplyingVisualEdits(true);
					try {
						const converted = await resolveSignedImageUrls(
							currentFrame.id,
							currentFrame.siteId,
							visualEditEntry.visualEdits
						);
						sendToDevigner(iframeRef.current, currentFrame.siteId, {
							type: 'applyVisualEdits',
							visualEdits: converted,
						});
					} finally {
						setIsApplyingVisualEdits(false);
					}
				}
			} else {
				sendToDevigner(iframeRef.current, currentFrame.siteId, {
					type: 'clearVisualEdits',
				});
			}
		},
		[siteUrl, iframeReady]
	);

	useEffect(() => {
		frameRef.current = frame;
	}, [frame]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	// Handle resize logic
	const onResizeStart = (e: React.MouseEvent, side: 'left' | 'right') => {
		e.preventDefault();
		setIsResizing(true);

		const startX = e.clientX;
		const startWidth = currentFrameSize.width;

		const handleMouseMove = (e: MouseEvent) => {
			const deltaX = e.clientX - startX;
			const scale = viewport?.scale;
			if (!scale) return;
			const deltaWidth = deltaX / scale;

			let newWidth = startWidth;
			if (side === 'right') {
				newWidth = Math.max(200, startWidth + deltaWidth);
			} else {
				newWidth = Math.max(200, startWidth - deltaWidth);
			}

			updateFrameSize({ width: newWidth, height: currentFrameSize.height });
		};

		const handleMouseUp = () => {
			setIsResizing(false);
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
	};

	const onResetFrameAfterError = async () => {
		await forge.updateFrame(
			{
				update: {
					id: frame.id,
					metadata: {
						...frame.metadata,
						status: FrameStatus.READY,
						statusMessage: '',
					},
				},
			},
			true
		);
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const renderStatusOverlay = () => {
		if (!frame.metadata.status || !viewport) return null;

		const frameReady = frame.metadata.status === FrameStatus.READY;
		if (frameReady && !isImageLoading && !isApplyingVisualEdits) return null;

		const scale = viewport.scale;

		// Applying visual edits isn't a frame status and carries no progress signal, so the moon
		// has nothing to phase through — the indeterminate loader stands in for it.
		const isVisualEditOnly = frameReady && !isImageLoading && isApplyingVisualEdits;

		return (
			<S.FrameStatusOverlay
				x={viewport.x}
				y={viewport.y}
				width={viewport.width}
				height={viewport.height}
				scale={scale}
			>
				<S.FrameStatusContent>
					{isVisualEditOnly ? (
						<Loader size={MOON_SIZE / scale} />
					) : (
						<FrameLoadingIndicator
							frame={frame}
							isImageLoading={isImageLoading}
							moon={moon}
							moonSize={MOON_SIZE / scale}
							fontSize={STATUS_SIZE / scale}
							gap={STATUS_GAP / scale}
						>
							{frame.metadata.status === FrameStatus.ERROR && (
								<S.ScaledButton scale={scale} marginTop={STATUS_GAP / scale}>
									<Button
										onClick={onResetFrameAfterError}
										label="Revert"
										type={ButtonType.SECONDARY}
									/>
								</S.ScaledButton>
							)}
						</FrameLoadingIndicator>
					)}
				</S.FrameStatusContent>
			</S.FrameStatusOverlay>
		);
	};

	const canvasColor = scene?.metadata?.canvasColor ?? DEFAULT_CANVAS_COLOR;

	// Get safe background color - handle CSS variables that don't work in this context
	const safeBackgroundColor = frame.metadata.backgroundColor?.startsWith('var(')
		? undefined // this will trigger the default when passed to FrameContainer
		: frame.metadata.backgroundColor;

	return (
		<S.PrototypeContainer ref={containerRef} backgroundColor={canvasColor}>
			{viewport && (siteUrl || imageSrc) && (
				<S.FrameContainer
					x={viewport.x}
					y={viewport.y}
					width={viewport.width}
					height={viewport.height}
					scale={viewport.scale}
					backgroundColor={safeBackgroundColor}
				>
					{siteUrl ? (
						<iframe
							key={iframeReloadKey}
							ref={iframeRef}
							src={siteUrl}
							title="Frame Prototype"
							style={{
								width: '100%',
								height: '100%',
								border: 'none',
								pointerEvents: isDragging || isResizing ? 'none' : 'auto',
							}}
						/>
					) : imageSrc ? (
						<img
							key={imageSrc}
							ref={handleImageRef}
							src={imageSrc}
							alt={frame.title || 'Frame'}
							style={{
								width: '100%',
								height: '100%',
								objectFit: 'contain',
								pointerEvents: isDragging || isResizing ? 'none' : 'auto',
							}}
						/>
					) : null}
				</S.FrameContainer>
			)}
			{renderStatusOverlay()}
			{!fullscreenMode && viewport && (siteUrl || imageSrc) && (
				<>
					{viewport.x > 20 && (
						<S.ResizeHandle
							side="left"
							x={viewport.x - 2}
							y={viewport.y}
							height={viewport.height * viewport.scale}
							onMouseDown={(e) => onResizeStart(e, 'left')}
						/>
					)}
					{containerSize.width - (viewport.x + viewport.width * viewport.scale) > 20 && (
						<S.ResizeHandle
							side="right"
							x={viewport.x + viewport.width * viewport.scale - 2}
							y={viewport.y}
							height={viewport.height * viewport.scale}
							onMouseDown={(e) => onResizeStart(e, 'right')}
						/>
					)}
				</>
			)}
			{imageReplaceTarget && (
				<ImageSelectedModal
					imageElement={imageReplaceTarget}
					onSubmit={onImageReplaceSubmit}
					onCancel={() => setImageReplaceTarget(null)}
				/>
			)}
		</S.PrototypeContainer>
	);
};

export default Prototype;
